import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  TextField,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Box,
  Tooltip,
  Chip,
  Divider,
  Collapse,
  InputAdornment
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as OkIcon,
  ErrorOutline as ErroIcon,
  Code as CodeIcon,
  VpnKey as ChaveIcon,
  ExpandMore as ExpandIcon,
  Search as BuscarIcon,
  PlayArrow as TestarIcon
} from "@material-ui/icons";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import Autocomplete from "@material-ui/lab/Autocomplete";
import ConfirmationModal from "../ConfirmationModal";
import { i18n } from "../../translate/i18n";
// O exemplo de conteúdo sai do mesmo catálogo que define os eventos — não há
// uma segunda lista no front para discordar dele.
import { EVENT_SAMPLES, sampleOf } from "@markedesk/plugin-sdk/ui";

/**
 * Destinos de callback da empresa.
 *
 * A empresa vem do token no backend — esta tela nunca a envia. Os eventos
 * disponíveis também vêm de lá: a empresa principal enxerga os eventos da
 * operação (cadastro de cliente novo), as demais só os seus.
 */

const useStyles = makeStyles(theme => ({
  cabecalho: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  subtitulo: {
    marginTop: 4,
    maxWidth: 620,
    lineHeight: 1.5
  },
  // Um cartão por destino: a lista é curta e cada linha tem informação demais
  // para uma tabela — evento, endereço, e como foi o último envio.
  cartao: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    transition: "border-color .15s, box-shadow .15s",
    "&:hover": {
      borderColor: theme.palette.primary.light,
      boxShadow: theme.shadows[1]
    }
  },
  cartaoInativo: { opacity: 0.55 },
  corpo: { flex: 1, minWidth: 0 },
  url: {
    fontFamily: "monospace",
    fontSize: 12,
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  vazio: {
    textAlign: "center",
    padding: theme.spacing(6, 3),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 8
  },
  statusOk: { color: theme.palette.success.main, fontSize: 18 },
  statusErro: { color: theme.palette.error.main, fontSize: 18 },
  secao: {
    display: "block",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5)
  },
  // O que o destino recebe. É o que um desenvolvedor precisa ver antes de
  // integrar — mais útil que qualquer descrição em prosa.
  exemplo: {
    margin: 0,
    padding: theme.spacing(1.5),
    background:
      theme.palette.type === "dark" ? "rgba(255,255,255,.04)" : "#f6f7f9",
    borderRadius: 6,
    fontFamily: "monospace",
    fontSize: 11,
    lineHeight: 1.6,
    overflowX: "auto",
    color: theme.palette.text.secondary
  },
  linkExemplo: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    cursor: "pointer",
    userSelect: "none",
    color: theme.palette.primary.main,
    fontSize: 12,
    marginBottom: theme.spacing(1)
  },
  filtros: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
    flexWrap: "wrap"
  },
  busca: { flex: 1, minWidth: 240 },
  filtro: { minWidth: 170 },
  metodo: {
    fontFamily: "monospace",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.5,
    padding: "2px 6px",
    borderRadius: 4,
    background:
      theme.palette.type === "dark" ? "rgba(255,255,255,.08)" : "#eceff1",
    color: theme.palette.text.secondary
  },
  girado: { transform: "rotate(180deg)" },
  seta: { fontSize: 16, transition: "transform .2s" }
}));

/**
 * Texto do evento no idioma do usuário.
 *
 * A tradução vence; o texto do catálogo (que vem da API) é a reserva. É o que
 * permite um plugin declarar evento próprio sem precisar traduzir para os
 * cinco idiomas — o rótulo dele aparece como veio.
 *
 * O nome do evento vira chave com "_" porque ":" é separador de namespace no
 * i18n e quebraria a busca.
 */
const traduzir = (evento, campo) => {
  if (!evento) return "";

  // 1) tradução do app (eventos do sistema)
  const chave = `eventCatalog.${evento.name.replace(":", "_")}.${campo}`;
  const traduzido = i18n.t(chave);
  if (traduzido !== chave) return traduzido;

  // 2) o que o plugin declarou. Ele pode mandar texto por idioma
  //    ({ pt: "...", en: "..." }) ou uma string só, que vale para todos.
  const declarado = evento[campo];
  if (declarado && typeof declarado === "object") {
    const idioma = i18n.language || "pt";
    return (
      declarado[idioma] ||
      declarado[idioma.split("-")[0]] ||
      declarado.en ||
      Object.values(declarado)[0] ||
      ""
    );
  }
  return declarado || "";
};

const traduzirGrupo = grupo => {
  // Grupo declarado por plugin também pode vir por idioma.
  if (grupo && typeof grupo === "object") {
    const idioma = i18n.language || "pt";
    return grupo[idioma] || grupo.en || Object.values(grupo)[0] || "";
  }
  const chave = `eventCatalog.groups.${grupo
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")}`;
  const traduzido = i18n.t(chave);
  return traduzido === chave ? grupo : traduzido;
};

/**
 * A requisição inteira, como ela chega no destino.
 *
 * O `data` traz o conteúdo REAL do evento escolhido, não um "...": é o que
 * permite escrever o código do outro lado antes do primeiro envio acontecer.
 * Sem isso, descobrir o formato exigia cadastrar, esperar o evento e ler o
 * que chegou — adivinhando até lá.
 *
 * O exemplo vem do catálogo (o plugin declara o dele). Evento sem exemplo cai
 * no genérico, que ao menos ensina o envelope.
 */
const exemploEnvio = (definicao, evento, url, temSegredo) => {
  let caminho = i18n.t("callbacks.samplePath");
  try {
    if (url) caminho = new URL(url).pathname || "/";
  } catch (e) {
    // URL ainda incompleta enquanto digita — mantém o texto de exemplo
  }

  const nome = evento || "ticket:created";
  const conteudo =
    sampleOf(definicao) ||
    EVENT_SAMPLES[nome] || { "...": i18n.t("callbacks.sampleContent") };

  const corpo = JSON.stringify(
    {
      event: nome,
      occurredAt: "2026-08-04T14:30:00.000Z",
      companyId: 1,
      data: conteudo
    },
    null,
    2
  );

  return [
    `POST ${caminho}`,
    "Content-Type: application/json",
    `X-Markedesk-Event: ${nome}`,
    // Identifica esta entrega: mesmo aviso reenviado traz o mesmo id, então o
    // outro lado consegue descartar repetido.
    "X-Markedesk-Delivery-Id: 3f2504e0-4f89-11d3-9a0c-0305e82c3301:7",
    temSegredo ? "X-Markedesk-Signature: 4f29a4e5de5f2325..." : null,
    "",
    corpo
  ]
    .filter(linha => linha !== null)
    .join("\n");
};

const vazio = { event: "", url: "", name: "", secret: "", active: true };

export default function CallbacksManager() {
  const classes = useStyles();

  const [callbacks, setCallbacks] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [modalAberto, setModalAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState(null);
  const [form, setForm] = useState(vazio);
  const [confirmando, setConfirmando] = useState(null);
  const [mostrarExemplo, setMostrarExemplo] = useState(false);
  const [busca, setBusca] = useState("");
  // Catálogo indexado por nome, para achar rótulo em O(1) na renderização.
  const catalogo = useMemo(
    () => Object.fromEntries(eventos.map(e => [e.name, e])),
    [eventos]
  );
  const rotulo = useCallback(
    nome => traduzir(catalogo[nome], "label") || nome,
    [catalogo]
  );
  const explicacao = useCallback(
    nome => traduzir(catalogo[nome], "description"),
    [catalogo]
  );
  const [filtroEvento, setFiltroEvento] = useState("todos");
  const [filtroSituacao, setFiltroSituacao] = useState("todos");
  const [testando, setTestando] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [lista, disponiveis] = await Promise.all([
        api.get("/event-callbacks"),
        api.get("/event-callbacks/events")
      ]);
      setCallbacks(lista.data);
      setEventos(disponiveis.data);
    } catch (err) {
      toastError(err);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Eventos que aparecem no filtro: só os que têm destino cadastrado — filtrar
  // por evento sem nenhum destino não leva a lugar nenhum.
  const eventosEmUso = useMemo(
    () => [...new Set(callbacks.map(c => c.event))],
    [callbacks]
  );

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return callbacks.filter(c => {
      if (filtroEvento !== "todos" && c.event !== filtroEvento) return false;
      if (filtroSituacao === "ativos" && !c.active) return false;
      if (filtroSituacao === "pausados" && c.active) return false;
      if (filtroSituacao === "falhando" && !(c.lastStatus >= 400 || c.lastError))
        return false;
      if (!termo) return true;
      return (
        c.url.toLowerCase().includes(termo) ||
        (c.name || "").toLowerCase().includes(termo) ||
        rotulo(c.event).toLowerCase().includes(termo)
      );
    });
  }, [callbacks, busca, filtroEvento, filtroSituacao]);

  const filtrando =
    busca.trim() !== "" || filtroEvento !== "todos" || filtroSituacao !== "todos";

  const urlInvalida = useMemo(
    () => form.url.length > 0 && !/^https?:\/\/.+/i.test(form.url),
    [form.url]
  );

  const abrir = callback => {
    setEmEdicao(callback);
    setForm(
      callback
        ? {
            event: callback.event,
            url: callback.url,
            name: callback.name || "",
            secret: "",
            active: callback.active
          }
        : vazio
    );
    setMostrarExemplo(false);
    setModalAberto(true);
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      if (emEdicao) {
        await api.put(`/event-callbacks/${emEdicao.id}`, form);
        toast.success(i18n.t("callbacks.saved"));
      } else {
        await api.post("/event-callbacks", form);
        toast.success(i18n.t("callbacks.created"));
      }
      setModalAberto(false);
      carregar();
    } catch (err) {
      toastError(err);
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async () => {
    try {
      await api.delete(`/event-callbacks/${confirmando.id}`);
      toast.success(i18n.t("callbacks.removed"));
      carregar();
    } catch (err) {
      toastError(err);
    } finally {
      setConfirmando(null);
    }
  };

  /**
   * Envia um aviso de teste. É como o operador descobre sozinho o que está
   * errado — sem isso, "nunca chegou" e "chegou e falhou" são indistinguíveis.
   */
  const testar = async callback => {
    setTestando(callback.id);
    try {
      const { data } = await api.post(`/event-callbacks/${callback.id}/test`);
      if (data.error) {
        toast.error(i18n.t("callbacks.testFailed", { error: data.error }), {
          autoClose: 8000
        });
      } else {
        toast.success(
          i18n.t("callbacks.testOk", {
            status: data.status,
            duration: data.durationMs
          })
        );
      }
      carregar();
    } catch (err) {
      toastError(err);
    } finally {
      setTestando(null);
    }
  };

  const alternarAtivo = async callback => {
    try {
      await api.put(`/event-callbacks/${callback.id}`, { active: !callback.active });
      carregar();
    } catch (err) {
      toastError(err);
    }
  };

  const ultimoEnvio = callback => {
    if (!callback.lastDeliveryAt) {
      return (
        <Typography variant="caption" color="textSecondary">
          {i18n.t("callbacks.waitingFirst")}
        </Typography>
      );
    }

    // A data segue o idioma do usuário; sem isso um usuário em inglês veria
    // dia/mês invertido em relação ao que o resto da tela diz.
    const quando = new Date(callback.lastDeliveryAt).toLocaleString(
      i18n.language || "pt-BR",
      {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      }
    );
    const deuCerto = callback.lastStatus && callback.lastStatus < 400;

    return (
      <Tooltip
        title={
          deuCerto
            ? i18n.t("callbacks.answered", { status: callback.lastStatus })
            : callback.lastError ||
              i18n.t("callbacks.answered", { status: callback.lastStatus })
        }
      >
        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
          {deuCerto ? (
            <OkIcon className={classes.statusOk} />
          ) : (
            <ErroIcon className={classes.statusErro} />
          )}
          <Typography variant="caption" color="textSecondary">
            {i18n.t(deuCerto ? "callbacks.received" : "callbacks.failed")} ·{" "}
            {quando}
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  return (
    <div>
      <div className={classes.cabecalho}>
        <div>
          <Typography variant="h6">{i18n.t("callbacks.title")}</Typography>
          <Typography variant="body2" color="textSecondary" className={classes.subtitulo}>
            {i18n.t("callbacks.subtitle")}
          </Typography>
        </div>
        {callbacks.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => abrir(null)}
            disableElevation
          >
            {i18n.t("callbacks.add")}
          </Button>
        )}
      </div>

      {carregando ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress />
        </Box>
      ) : callbacks.length === 0 ? (
        <div className={classes.vazio}>
          <Typography variant="subtitle1" gutterBottom>
            {i18n.t("callbacks.emptyTitle")}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
            {i18n.t("callbacks.emptyText")}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => abrir(null)}
            disableElevation
          >
            {i18n.t("callbacks.add")}
          </Button>
        </div>
      ) : (
        <>
          {/* Filtros só aparecem quando há o que filtrar. */}
          {callbacks.length > 3 && (
            <Box className={classes.filtros}>
              <TextField
                placeholder={i18n.t("callbacks.search")}
                variant="outlined"
                size="small"
                value={busca}
                onChange={e => setBusca(e.target.value)}
                className={classes.busca}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BuscarIcon style={{ fontSize: 18, opacity: 0.5 }} />
                    </InputAdornment>
                  )
                }}
              />
              <FormControl variant="outlined" size="small" className={classes.filtro}>
                <InputLabel>{i18n.t("callbacks.event")}</InputLabel>
                <Select
                  label={i18n.t("callbacks.event")}
                  value={filtroEvento}
                  onChange={e => setFiltroEvento(e.target.value)}
                >
                  <MenuItem value="todos">{i18n.t("callbacks.allEvents")}</MenuItem>
                  {eventosEmUso.map(evento => (
                    <MenuItem key={evento} value={evento}>
                      {rotulo(evento)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" className={classes.filtro}>
                <InputLabel>{i18n.t("callbacks.situation")}</InputLabel>
                <Select
                  label={i18n.t("callbacks.situation")}
                  value={filtroSituacao}
                  onChange={e => setFiltroSituacao(e.target.value)}
                >
                  <MenuItem value="todos">{i18n.t("callbacks.all")}</MenuItem>
                  <MenuItem value="ativos">{i18n.t("callbacks.receiving")}</MenuItem>
                  <MenuItem value="pausados">{i18n.t("callbacks.paused")}</MenuItem>
                  <MenuItem value="falhando">{i18n.t("callbacks.failing")}</MenuItem>
                </Select>
              </FormControl>
              {filtrando && (
                <Button
                  size="small"
                  onClick={() => {
                    setBusca("");
                    setFiltroEvento("todos");
                    setFiltroSituacao("todos");
                  }}
                >
                  {i18n.t("callbacks.clear")}
                </Button>
              )}
            </Box>
          )}

          {filtrados.length === 0 ? (
            <Box className={classes.vazio}>
              <Typography variant="body2" color="textSecondary">
                {i18n.t("callbacks.noMatch")}
              </Typography>
            </Box>
          ) : (
            filtrados.map(callback => (
          <Paper
            key={callback.id}
            elevation={0}
            className={`${classes.cartao} ${
              callback.active ? "" : classes.cartaoInativo
            }`}
          >
            <div className={classes.corpo}>
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Typography variant="subtitle2">{rotulo(callback.event)}</Typography>
                {/* O evento saiu do catálogo (plugin removido, por exemplo):
                    o destino segue cadastrado e nunca mais recebe nada. */}
                {callback.orphan && (
                  <Tooltip title={i18n.t("callbacks.orphanTooltip")}>
                    <Chip
                      size="small"
                      label={i18n.t("callbacks.orphanChip")}
                      color="secondary"
                      variant="outlined"
                    />
                  </Tooltip>
                )}
                {callback.name && (
                  <Chip size="small" variant="outlined" label={callback.name} />
                )}
                {callback.hasSecret && (
                  <Tooltip title={i18n.t("callbacks.signedTooltip")}>
                    <ChaveIcon style={{ fontSize: 14, opacity: 0.5 }} />
                  </Tooltip>
                )}
              </Box>
              <Typography className={classes.url}>{callback.url}</Typography>
            </div>

            <Box minWidth={170}>{ultimoEnvio(callback)}</Box>

            <Tooltip
              title={i18n.t(
                callback.active ? "callbacks.activeTooltip" : "callbacks.pausedTooltip"
              )}
            >
              <Switch
                size="small"
                checked={callback.active}
                onChange={() => alternarAtivo(callback)}
                color="primary"
              />
            </Tooltip>

            <Tooltip title={i18n.t("callbacks.testTooltip")}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => testar(callback)}
                  disabled={testando === callback.id}
                >
                  {testando === callback.id ? (
                    <CircularProgress size={16} />
                  ) : (
                    <TestarIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            <IconButton size="small" onClick={() => abrir(callback)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setConfirmando(callback)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
              </Paper>
            ))
          )}
        </>
      )}

      <Dialog
        open={modalAberto}
        onClose={() => setModalAberto(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle disableTypography>
          <Typography variant="h6">
            {i18n.t(emEdicao ? "callbacks.editTitle" : "callbacks.newTitle")}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {i18n.t("callbacks.modalSubtitle")}
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Typography className={classes.secao}>
            {i18n.t("callbacks.sectionWhen")}
          </Typography>
          {/* Campo com busca: com 11 eventos e crescendo, digitar chega mais
              rápido do que rolar a lista. */}
          <Autocomplete
            options={eventos}
            value={catalogo[form.event] || null}
            onChange={(_, valor) => setForm({ ...form, event: valor?.name || "" })}
            getOptionLabel={evento => traduzir(evento, "label")}
            groupBy={evento => traduzirGrupo(evento.group)}
            // O que ainda não acontece aparece, mas não pode ser escolhido:
            // esconder daria a entender que não existe; oferecer seria mentir.
            getOptionDisabled={evento => evento.status !== "available"}
            // Busca pelo nome em português e pelo nome técnico: quem integra
            // conhece o evento por "ticket:created", quem opera por
            // "atendimento".
            filterOptions={(opcoes, { inputValue }) => {
              const termo = inputValue.trim().toLowerCase();
              if (!termo) return opcoes;
              // Busca no texto traduzido E no nome técnico: quem opera procura
              // por "atendimento", quem integra por "ticket:created".
              return opcoes.filter(
                evento =>
                  traduzir(evento, "label").toLowerCase().includes(termo) ||
                  traduzir(evento, "description").toLowerCase().includes(termo) ||
                  evento.name.toLowerCase().includes(termo)
              );
            }}
            renderOption={evento => (
              <Box py={0.25}>
                <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                  <Typography variant="body2">{traduzir(evento, "label")}</Typography>
                  {evento.status !== "available" && (
                    <Chip
                      size="small"
                      label={i18n.t("callbacks.soon")}
                      variant="outlined"
                    />
                  )}
                </Box>
                <Typography variant="caption" color="textSecondary">
                  {traduzir(evento, "description")}
                </Typography>
              </Box>
            )}
            renderInput={params => (
              <TextField
                {...params}
                label={i18n.t("callbacks.event")}
                placeholder={i18n.t("callbacks.searchHint")}
                variant="outlined"
                size="small"
              />
            )}
            noOptionsText={i18n.t("callbacks.noEvent")}
          />
          {form.event && (
            <Typography variant="caption" color="textSecondary" component="p" style={{ marginTop: 6 }}>
              {i18n.t("callbacks.weWarnWhen", {
                description: explicacao(form.event)
              })}
            </Typography>
          )}

          <Box mt={3}>
            <Typography className={classes.secao}>
              {i18n.t("callbacks.sectionWhere")}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={i18n.t("callbacks.url")}
                  placeholder={i18n.t("callbacks.urlPlaceholder")}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={form.url}
                  error={urlInvalida}
                  helperText={
                    urlInvalida
                      ? i18n.t("callbacks.urlInvalid")
                      : i18n.t("callbacks.urlHelp")
                  }
                  onChange={e => setForm({ ...form, url: e.target.value })}
                  InputProps={{
                    // O método faz parte do contrato: quem cadastra precisa
                    // saber que vai receber POST, não GET.
                    startAdornment: (
                      <InputAdornment position="start">
                        <span className={classes.metodo}>POST</span>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={i18n.t("callbacks.description")}
                  placeholder={i18n.t("callbacks.descriptionPlaceholder")}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>

          <Box mt={3}>
            <Typography className={classes.secao}>
              {i18n.t("callbacks.sectionSecurity")}
            </Typography>
            <TextField
              label={i18n.t("callbacks.secret")}
              variant="outlined"
              size="small"
              fullWidth
              value={form.secret}
              onChange={e => setForm({ ...form, secret: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ChaveIcon style={{ fontSize: 16, opacity: 0.5 }} />
                  </InputAdornment>
                )
              }}
              placeholder={i18n.t(
                emEdicao?.hasSecret
                  ? "callbacks.secretUnchanged"
                  : "callbacks.secretOptional"
              )}
              helperText={
                emEdicao?.hasSecret
                  ? i18n.t("callbacks.secretExists")
                  : i18n.t("callbacks.secretHelp")
              }
            />
          </Box>

          <Divider style={{ margin: "20px 0 12px" }} />

          <div
            className={classes.linkExemplo}
            onClick={() => setMostrarExemplo(v => !v)}
          >
            <CodeIcon style={{ fontSize: 16 }} />
            {i18n.t("callbacks.seeRequest")}
            <ExpandIcon
              className={`${classes.seta} ${mostrarExemplo ? classes.girado : ""}`}
            />
          </div>
          <Collapse in={mostrarExemplo}>
            <pre className={classes.exemplo}>
              {exemploEnvio(
                catalogo[form.event],
                form.event,
                form.url,
                !!form.secret
              )}
            </pre>
          </Collapse>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setModalAberto(false)} disabled={salvando}>
            {i18n.t("callbacks.cancel")}
          </Button>
          <Button
            onClick={salvar}
            color="primary"
            variant="contained"
            disableElevation
            disabled={salvando || !form.event || !form.url || urlInvalida}
          >
            {salvando ? (
              <CircularProgress size={20} />
            ) : (
              i18n.t(emEdicao ? "callbacks.save" : "callbacks.create")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title={i18n.t("callbacks.removeTitle")}
        open={!!confirmando}
        onClose={() => setConfirmando(null)}
        onConfirm={excluir}
      >
        {i18n.t("callbacks.removeText", {
          url: confirmando?.url || "",
          event: confirmando ? rotulo(confirmando.event).toLowerCase() : ""
        })}
      </ConfirmationModal>
    </div>
  );
}
